import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const UploadZone = () => {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const pickFile = useCallback((f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Carica un file PDF");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File troppo grande (max 25MB)");
      return;
    }
    setFile(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const handleAnalyze = async () => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const doc = await uploadDocument(file);
      toast.success("Contratto caricato. Analisi in corso...");
      navigate(`/analisi/${doc.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-8 md:p-10 text-center cursor-pointer transition-all min-h-[280px] flex flex-col items-center justify-center ${
          dragActive
            ? "upload-zone-active"
            : "border-slate-300 bg-slate-50 hover:border-slate-500 hover:bg-slate-100"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => pickFile(e.target.files?.[0])}
        />

        {file ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-700" />
            </div>
            <h3 className="mt-4 font-display text-xl font-bold tracking-tight" data-testid="selected-filename">
              {file.name}
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              {(file.size / 1024).toFixed(1)} KB · clicca per cambiare file
            </p>
          </>
        ) : (
          <>
            <div className="relative w-16 h-16 flex items-center justify-center">
              <div className="absolute inset-0 bg-emerald-500/10 rounded-full pulse-ring" />
              <div className="relative w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                <UploadCloud className="w-6 h-6 text-slate-900" />
              </div>
            </div>

            <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
              Trascina qui il tuo <span className="text-emerald-600">PDF</span>
            </h3>
            <p className="text-slate-500 mt-2 max-w-sm text-sm">
              Contratto d'appalto, subappalto o capitolato. Lo analizziamo in pochi secondi.
            </p>

            <div className="mt-5 flex items-center gap-4 text-xs text-slate-400 uppercase tracking-wider font-semibold">
              <span>PDF · max 25MB</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span>Analisi in 30-60 sec</span>
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        data-testid="analyze-btn"
        disabled={!file || uploading}
        onClick={handleAnalyze}
        className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-bold px-6 py-3.5 rounded-md transition-colors"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Caricamento...
          </>
        ) : (
          <>
            <Sparkles size={16} />
            Analizza contratto
          </>
        )}
      </button>
    </div>
  );
};

export default UploadZone;
